<template>
  <div class="maintainance-plan-machine-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('maintainancePlanMachine.page.title')"
      :subtitle="$t('maintainancePlanMachine.page.subtitle')">
      <template #extra>
        <el-button icon="el-icon-back" @click="_goBack">
          {{ $t('maintainancePlanMachine.back') }}
        </el-button>
        <el-button type="primary" icon="el-icon-plus" @click="_openAddMaintainancePlanMachineModal">
          {{ $t('maintainancePlanMachine.add') }}
        </el-button>
      </template>
    </ds-page-header>

    <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
    <div class="ds-panel">
      <ds-toolbar :total="pagination.total" :density="density" :show-column-setting="false" :show-refresh="true"
        @update:density="density = $event" @refresh="_listMaintainancePlanMachines">
        <template #left>
          <span class="ds-panel__title">{{ $t('maintainancePlanMachine.title') }}</span>
        </template>
      </ds-toolbar>

      <ds-data-table :data="maintainancePlanMachineInfo.machines" :loading="loading" :error="hasError"
        :error-message="errorMessage" :density="density" border
        :empty-description="$t('maintainancePlanMachine.page.empty')" @retry="_listMaintainancePlanMachines">
        <el-table-column prop="machineName" :label="$t('maintainancePlanMachine.machineName')" align="left" min-width="180"
          show-overflow-tooltip>
          <template slot-scope="scope">
            {{ scope.row.machineName || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="machineId" :label="$t('maintainancePlanMachine.machineId')" align="center" width="160">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.machineId || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('maintainancePlanMachine.operation')" align="center" width="100" fixed="right">
          <template slot-scope="scope">
            <el-button size="mini" type="text" class="ds-btn-danger"
              @click="_openDeleteMaintainancePlanMachineModel(scope.row)">
              {{ $t('maintainancePlanMachine.delete') }}
            </el-button>
          </template>
        </el-table-column>
      </ds-data-table>

      <ds-pagination-bar :total="pagination.total" :page="pagination.current" :size="pagination.size"
        :page-sizes="[10, 20, 30, 50]" @size-change="handleSizeChange" @current-change="handleCurrentChange">
        <template #summary>
          <span class="ds-tip-text">{{ $t('maintainancePlanMachine.page.tip') }}</span>
        </template>
      </ds-pagination-bar>
    </div>

    <choose-maintainance-plan-machine ref="chooseMachineModal"
      @success="handleSuccess"></choose-maintainance-plan-machine>
    <delete-maintainance-plan-machine ref="deleteMachineModal"
      @success="handleSuccess"></delete-maintainance-plan-machine>
  </div>
</template>

<script>
import { listMaintainancePlanMachine } from '@/api/inspection/maintainancePlanMachineApi'
import ChooseMaintainancePlanMachine from '@/components/inspection/chooseMaintainancePlanMachine'
import DeleteMaintainancePlanMachine from '@/components/inspection/deleteMaintainancePlanMachine'
import { getCommunityId } from '@/api/community/communityApi'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'

export default {
  name: 'MaintainancePlanMachineList',
  components: {
    ChooseMaintainancePlanMachine,
    DeleteMaintainancePlanMachine,
    DsPageHeader,
    DsToolbar,
    DsDataTable,
    DsPaginationBar
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      maintainancePlanMachineInfo: {
        machines: [],
        planId: '',
        routeName: ''
      },
      pagination: {
        current: 1,
        size: 10,
        total: 0
      }
    }
  },
  created() {
    this.maintainancePlanMachineInfo.planId = this.$route.query.planId
    this._listMaintainancePlanMachines()
  },
  methods: {
    async _listMaintainancePlanMachines() {
      this.loading = true
      this.hasError = false
      this.errorMessage = ''
      try {
        const params = {
          page: this.pagination.current,
          row: this.pagination.size,
          communityId: getCommunityId(),
          planId: this.maintainancePlanMachineInfo.planId
        }
        const { data, records } = await listMaintainancePlanMachine(params)
        this.maintainancePlanMachineInfo.machines = Array.isArray(data) ? data : []
        this.pagination.total = records || 0
      } catch (error) {
        console.error('获取保养计划设备失败:', error)
        this.hasError = true
        this.errorMessage = this.$t('maintainancePlanMachine.page.loadFailed')
        this.$message.error(this.$t('maintainancePlanMachine.fetchError'))
      } finally {
        this.loading = false
      }
    },
    _openAddMaintainancePlanMachineModal() {
      this.$refs.chooseMachineModal.open({
        planId: this.maintainancePlanMachineInfo.planId
      })
    },
    _openDeleteMaintainancePlanMachineModel(machine) {
      this.$refs.deleteMachineModal.open({
        ...machine,
        planId: this.maintainancePlanMachineInfo.planId
      })
    },
    _goBack() {
      this.$router.go(-1)
    },
    handleSuccess() {
      this._listMaintainancePlanMachines()
    },
    handleSizeChange(val) {
      this.pagination.size = val
      this.pagination.current = 1
      this._listMaintainancePlanMachines()
    },
    handleCurrentChange(val) {
      this.pagination.current = val
      this._listMaintainancePlanMachines()
    }
  }
}
</script>

<style lang="scss" scoped>
.maintainance-plan-machine-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

/* 表格 + 分页 组成的白色面板 */
.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}

/* 分页条左侧提示文案 */
.ds-tip-text {
  font-size: var(--ds-font-size-xs);
  color: var(--ds-gray-500);
  line-height: 1.6;
}

/* 危险操作文字按钮 */
.ds-btn-danger {
  color: var(--ds-danger) !important;
}
</style>
